import { Link } from "@tanstack/react-router";
import { ArrowLeft, Calendar, Clock } from "lucide-react";
import ReactMarkdown from "react-markdown";

import { DownloadCta } from "@/components/landing/DownloadCta";
import type { BlogPost } from "@/content/blog";
import { leadMagnetPromoForBlogPost } from "@/content/lead-magnet-links";
import { pages } from "@/content/pages";
import type { Locale } from "@/i18n/locales";
import { localePath } from "@/i18n/locales";

type BlogPostViewProps = {
  locale: Locale;
  post: BlogPost;
};

export function BlogPostView({ locale, post }: BlogPostViewProps) {
  const copy = pages[locale].blog;
  const app = pages[locale].app;
  const promo = leadMagnetPromoForBlogPost(locale, post.slug);

  return (
    <main className="bg-background pb-24 pt-32">
      <article className="mx-auto max-w-3xl px-5">
        <Link
          to={localePath(locale, "/blog")}
          className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground transition-colors hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" /> {copy.backToBlog}
        </Link>
        <p className="mb-4 text-xs font-bold uppercase tracking-widest text-primary">
          {post.category}
        </p>
        <h1 className="mb-6 font-heading text-3xl font-bold leading-tight tracking-tight text-[#1a3a5c] md:text-5xl">
          {post.title}
        </h1>
        <div className="mb-8 flex items-center gap-5 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <Calendar className="h-4 w-4" />
            {post.date}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="h-4 w-4" /> {post.readTime}
          </span>
          <span className="font-medium text-foreground">{post.author}</span>
        </div>
        <div className="mb-12 overflow-hidden rounded-2xl border border-border shadow-sm">
          <img
            src={post.image}
            alt={post.title}
            width={1600}
            height={900}
            loading="lazy"
            className="h-64 w-full object-cover md:h-80"
          />
        </div>
        <div className="markdown-content">
          <ReactMarkdown>{post.content}</ReactMarkdown>
        </div>
        {promo ? (
          <section className="mt-16 rounded-3xl border border-border bg-card p-8 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-widest text-primary">
              {promo.eyebrow}
            </p>
            <h2 className="mt-4 font-heading text-2xl font-bold leading-tight text-[#1a3a5c] md:text-3xl">
              {promo.title}
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
              {promo.text}
            </p>
            <Link
              to={promo.href}
              className="mt-6 inline-flex items-center rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              {promo.cta}
            </Link>
          </section>
        ) : null}
      </article>
      <DownloadCta
        locale={locale}
        title={app.downloadTitle}
        text={app.downloadText}
        className="pt-8"
      />
    </main>
  );
}
